import { iframeResizer } from 'iframe-resizer'
import { hideIframeLoader, showIframeLoader } from './iframeLoader'

// Post-init height floor. iframe-resizer writes every measured height as an
// inline style with no lower bound of its own, and it can legitimately
// measure ~0 (e.g. the widget loaded inside a hidden tab/accordion, so its
// document has no layout). No real widget renders shorter than this, so
// keeping a small floor guarantees a bogus measurement can never collapse
// the widget to a horizontal line.
const MIN_WIDGET_HEIGHT = '150px'

// iframe-resizer applies scrolling="no" + overflow:hidden the moment we call
// it - before its handshake completes. A healthy handshake finishes well
// under a second after the iframe's load event, so if initCallback still
// hasn't fired by this deadline the widget is pinned at the loading floor
// with its content clipped and unreachable. Generous to absorb slow devices;
// only ever observed on hostile host pages (e.g. script-delaying optimizers).
const HANDSHAKE_TIMEOUT_MS = 8000

// Host CSS can clamp the iframe below the height iframe-resizer measured
// (e.g. an !important height rule, which injected styles can't beat). The
// tolerance keeps borders, sub-pixel rounding, and mild ancestor transforms
// from reading as a clamp.
const CLAMPED_HEIGHT_TOLERANCE_PX = 50

// A rendered width at or below this is never an intentional layout - the
// injected stylesheet floors every healthy layout at >=250px - so it marks
// the widget as collapsed (1px base width sliver, or a hidden container's
// 0px). Shared by the floor escalation and the reveal re-measure so a width
// sitting exactly on the boundary can't escalate without also re-measuring.
const COLLAPSED_WIDTH_PX = 2

export function injectIframe(
  iframe: HTMLIFrameElement,
  widgetSnippet: Element,
) {
  showIframeLoader(widgetSnippet)
  widgetSnippet.appendChild(iframe)

  // Wait for the iframe to navigate to its src before initializing iframe-resizer.
  // Without this, iframe-resizer's init posts a message to `iframe.contentWindow`
  // with `targetOrigin: <IFRAME_URL origin>`, but the contentWindow is still on
  // `about:blank` (which inherits the parent page's origin), so the browser
  // rejects the postMessage with "target origin does not match recipient origin".
  // The handshake never completes and the iframe stays at height: 0. The race
  // is sensitive to network and main-thread contention on the host page, which
  // is why customers see this as an intermittent collapse.
  iframe.addEventListener(
    'load',
    () => {
      let scrollingFallbackCause: 'handshake-timeout' | 'host-clamp' | null =
        null

      // Last-resort recovery when the widget is stuck clipped: restore native
      // iframe scrolling so the content below the fold is at least reachable.
      // The widget document never disables its own overflow, so undoing the
      // parent-side scrolling="no" is sufficient. Never fires on a healthy
      // embed - there the iframe always exactly fits its content.
      const enableScrollingFallback = (
        cause: 'handshake-timeout' | 'host-clamp',
      ) => {
        if (scrollingFallbackCause) return
        scrollingFallbackCause = cause
        // A clamp fallback makes the pending handshake timer redundant (and
        // it would no-op against the guard above anyway) - drop it.
        clearTimeout(handshakeTimer)
        iframe.scrolling = 'auto'
        iframe.style.overflow = 'auto'
        hideIframeLoader(widgetSnippet)
      }

      const handshakeTimer = setTimeout(
        () => enableScrollingFallback('handshake-timeout'),
        HANDSHAKE_TIMEOUT_MS,
      )

      // The height our own iframe-resizer instance last wrote. Third-party
      // embeds can bundle their own iframe-resizer parent that resolves ANY
      // [iFrameSizer] message via document.getElementById - no ownership
      // check - and stamps the parsed height onto whatever element it finds,
      // including our iframe (seen live: LeadConnector's form_embed.js
      // misreading our child's messages and writing a ~0 height). Track our
      // authoritative height so the guardian below can undo foreign writes.
      let lastAppliedHeight: string | null = null

      const [resizerIframe] = iframeResizer(
        {
          log: false,
          // We never use onMouseEnter/onMouseLeave, and the mouse messages
          // the child emits for them carry 0:0 dimensions - exactly what
          // foreign parents misapply as a zero-height resize (they predate
          // v4's mouse events, so the type falls through to their resize
          // path). No mouse events, no bait. v4 defaults to true.
          mouseEvents: false,
          initCallback: () => {
            clearTimeout(handshakeTimer)
            // Only undo a timeout-caused fallback: a late handshake (e.g. a
            // host optimizer swapped the iframe src back in) makes
            // iframe-resizer authoritative again, so restore its no-scroll
            // invariant. A host-clamp fallback must survive init - on the
            // init message onResized fires before onInit, so a clamp detected
            // on the first measurement would otherwise be reverted here.
            if (scrollingFallbackCause === 'handshake-timeout') {
              scrollingFallbackCause = null
              iframe.scrolling = 'no'
              iframe.style.overflow = 'hidden'
            }
            hideIframeLoader(widgetSnippet)
            // Lower the loading-state 600px min-height floor now that
            // iframe-resizer has measured the real content height, so widgets
            // shorter than 600px can shrink to fit. Keep MIN_WIDGET_HEIGHT
            // instead of clearing to 0: a zero-height measurement arriving
            // after init must never fully collapse the widget. Skip when the
            // floor was escalated to !important (same value): a plain
            // assignment would silently drop the priority and hand the floor
            // back to the hostile host rule it was escalated to beat.
            if (
              iframe.style.getPropertyPriority('min-height') !== 'important'
            ) {
              iframe.style.minHeight = MIN_WIDGET_HEIGHT
            }
          },
          resizedCallback: (data) => {
            // setSize() wrote exactly `height + 'px'` inline just before
            // this fires; record it as the authoritative height.
            lastAppliedHeight = `${data.height}px`
            // A rendered height meaningfully below the measurement means
            // host CSS is clamping the iframe and clipping the widget.
            const renderedHeight = iframe.getBoundingClientRect().height
            if (data.height - renderedHeight > CLAMPED_HEIGHT_TOLERANCE_PX) {
              enableScrollingFallback('host-clamp')
            }
          },
        },
        `#${iframe.id}`,
      )

      // Undo foreign inline-height writes (see lastAppliedHeight above).
      // Restoring to the same value we compare against means our own write
      // re-triggers the observer exactly once and then settles - no loop.
      // Until our instance has applied a height there is nothing
      // authoritative to restore, and the loading-state floors bound any
      // damage a foreign write could do.
      if (typeof MutationObserver !== 'undefined') {
        const heightGuardian = new MutationObserver(() => {
          if (
            lastAppliedHeight !== null &&
            iframe.style.height !== lastAppliedHeight
          ) {
            iframe.style.height = lastAppliedHeight
          }
        })
        heightGuardian.observe(iframe, {
          attributes: true,
          attributeFilter: ['style'],
        })
      }

      // Host CSS can zero our min-width/min-height floors with !important
      // (both the injected stylesheet and plain inline styles lose that
      // fight), collapsing the widget to a 1px-wide sliver whose honest
      // ~0 measurement evades the handshake and clamp guards above. The one
      // cascade level a host stylesheet cannot beat is an !important
      // declaration on the style attribute, so re-assert the floors there.
      // Only on detection: a collapsed width is never intentional (the
      // stylesheet floor guarantees >=250px on every healthy layout), and
      // hosts that restyle the widget within sane bounds keep that ability.
      const escalateFloorsToImportant = () => {
        iframe.style.setProperty('min-width', 'max(100%, 250px)', 'important')
        if (!iframe.style.minWidth) {
          // Browser without max() support: keep the fixed half of the floor.
          iframe.style.setProperty('min-width', '250px', 'important')
        }
        iframe.style.setProperty('min-height', MIN_WIDGET_HEIGHT, 'important')
      }

      // If the widget loaded while its container was hidden (display: none -
      // e.g. an inactive tab or collapsed accordion), the iframe's document
      // has no layout, iframe-resizer measures ~0, and nothing inside the
      // iframe reliably fires again when the host reveals it. Watch the
      // iframe's rendered size from the parent side and force a re-measure
      // the moment it first gains real width. The same observer detects the
      // collapsed-width state; a hidden-at-load widget also lands in the
      // escalation branch, harmlessly - the escalated floors carry the same
      // values as the ones already in force.
      if (typeof ResizeObserver !== 'undefined') {
        let lastWidth = iframe.getBoundingClientRect().width
        let floorsEscalated = false
        const observer = new ResizeObserver((entries) => {
          const { width } = entries[entries.length - 1].contentRect
          if (width <= COLLAPSED_WIDTH_PX && !floorsEscalated) {
            floorsEscalated = true
            escalateFloorsToImportant()
          }
          if (width > COLLAPSED_WIDTH_PX && lastWidth <= COLLAPSED_WIDTH_PX) {
            resizerIframe?.iFrameResizer.resize()
          }
          lastWidth = width
        })
        observer.observe(iframe)
      }
    },
    { once: true },
  )
}
